import {
  AuctionBiddingStatus,
  AuctionResponse,
  AuctionType,
  SaleStatus,
} from '@bonhams1793/bonhams-typescript'
import getConfig from 'next/config'
import { memo, useCallback, useEffect, useState } from 'react'

import { LotResponse } from '@nx/api'
import {
  AMConfig,
  AMConnectionStatus,
  EAMConnectionStatus,
  useInitAuctionMobility,
} from '@nx/external/auction-mobility'
import { MaintenanceMode } from '@nx/global-types'

import { useBiddingConnectionNotification } from '@web/helpers/hooks/useBiddingConnectionNotification/useBiddingConnectionNotification'

import { AMWidgetWrapper } from './AMWidget.styles'

const { publicRuntimeConfig } = getConfig()

export interface AMWidgetProps {
  auction: AuctionResponse
  lot: LotResponse
  options: {
    showCountdown: boolean
    isUserLoggedIn: boolean
    hasHammerPrice: boolean
    hideLostConnectionNotification?: boolean
    onAmConnectionChange?: (status: EAMConnectionStatus) => void
  }
}

export const AMWidget = memo(
  function AMWidget({
    auction,
    lot,
    options,
  }: AMWidgetProps): JSX.Element | null {
    const [showBidding, setshowBidding] = useState<boolean>(false)
    const [amIsConnected, setAmIsConnected] = useState<boolean>(true)

    /* istanbul ignore next */
    const handleAmConnection = useCallback(
      (status: EAMConnectionStatus) => {
        if (
          status === AMConnectionStatus.disconnected ||
          status === AMConnectionStatus.errored
        ) {
          setAmIsConnected(false)
        } else if (status === AMConnectionStatus.connected) {
          setAmIsConnected(true)
        }

        options.onAmConnectionChange?.(status)
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [options.onAmConnectionChange]
    )

    useEffect(() => {
      const checkOne =
        options.isUserLoggedIn &&
        auction.sBidOnlineStatus === AuctionBiddingStatus.ended
      const checkTwo =
        auction.sSaleType === AuctionType.online
          ? auction.sBidOnlineStatus === AuctionBiddingStatus.inProgress
          : auction.sSaleStatus === SaleStatus.ready &&
            auction.sBidOnlineStatus !== AuctionBiddingStatus.ended
      const doNotRender =
        publicRuntimeConfig.MAINTENANCE_MODE !== MaintenanceMode.partial &&
        (checkOne || checkTwo)

      setshowBidding(doNotRender)
    }, [auction, options])

    useInitAuctionMobility({
      config: {
        maintenanceMode: publicRuntimeConfig.MAINTENANCE_MODE,
        paths: publicRuntimeConfig.AM_PATHS,
      },
      showBidding,
      amRowId: lot.amsale_rowid,
      lotIds: [lot.rowid],
      connectionStatusCallback: handleAmConnection,
    })

    useBiddingConnectionNotification({
      isAMConnected: amIsConnected && !options.hideLostConnectionNotification,
      biddingEngine: 'AM',
    })

    if (!showBidding) return null

    return (
      <AMWidgetWrapper $hasHammerPrice={options.hasHammerPrice}>
        <div
          id={`${AMConfig.wrapperPrefix}-${lot.rowid}`}
          lot-id={lot.rowid}
          className="am-data"
        >
          {options.showCountdown && (
            <div>
              <am-timer></am-timer>
            </div>
          )}
          <div>
            <am-lot-status-label></am-lot-status-label>
            <am-bid-status-label></am-bid-status-label>
          </div>
          <div>
            <am-bid-box></am-bid-box>
          </div>
        </div>
      </AMWidgetWrapper>
    )
  },
  (prevProps: AMWidgetProps, nextProps: AMWidgetProps) => {
    return JSON.stringify(prevProps) === JSON.stringify(nextProps)
  }
)
